import { useEffect, useState, useCallback } from 'react'
import { funnelApi } from '../api'
import { getErrorMessage } from '../lib/api-error'
import type { ApiError } from '../lib/api-error'
import type { Funnel, FunnelStep } from '../types'

export function useFunnelDetail(funnelId: string | undefined) {
  const [funnel, setFunnel] = useState<Funnel | null>(null)
  const [steps, setSteps] = useState<FunnelStep[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<'logic' | 'uxui'>('logic')

  const fetchFunnelData = useCallback(async () => {
    if (!funnelId) return
    try {
      setLoading(true)
      setError(null)
      const funnelResponse = await funnelApi.get(funnelId)
      setFunnel(funnelResponse.data)

      let stepsArray: FunnelStep[] = []
      try {
        const stepsResponse = await funnelApi.listSteps(funnelId)
        stepsArray = Array.isArray(stepsResponse.data) ? stepsResponse.data : []
      } catch {
        stepsArray = Array.isArray(funnelResponse.data.steps) ? funnelResponse.data.steps : []
      }
      stepsArray.sort((a, b) => a.position - b.position)
      setSteps(stepsArray)
    } catch (err) {
      setError(getErrorMessage(err as ApiError, 'Failed to load funnel.'))
    } finally {
      setLoading(false)
    }
  }, [funnelId])

  useEffect(() => {
    fetchFunnelData()
  }, [fetchFunnelData])

  const updateFunnelLocal = useCallback((updates: Partial<Funnel>) => {
    setFunnel((prev) => prev ? { ...prev, ...updates } : prev)
  }, [])

  const saveFunnel = useCallback(async () => {
    if (!funnel || !funnelId) return
    setSaving(true)
    setSaveError(null)
    try {
      await funnelApi.update(funnelId, {
        name: funnel.name,
        description: funnel.description || undefined,
        priority: funnel.priority,
        isActive: funnel.isActive,
        funnelType: funnel.funnelType,
        trafficType: funnel.trafficType,
        countries: funnel.countries,
        landingPageUrls: funnel.landingPageUrls,
        productId: funnel.productId,
      })
      await fetchFunnelData()
    } catch (err) {
      const message = getErrorMessage(err as ApiError, 'Failed to save funnel.')
      setSaveError(message)
      throw err
    } finally {
      setSaving(false)
    }
  }, [funnel, funnelId, fetchFunnelData])

  const deleteFunnel = useCallback(async () => {
    if (!funnelId) return
    try {
      await funnelApi.delete(funnelId)
    } catch (err) {
      const message = getErrorMessage(err as ApiError, 'Failed to delete funnel.')
      setSaveError(message)
      throw err
    }
  }, [funnelId])

  return {
    funnel,
    steps,
    loading,
    error,
    saveError,
    saving,
    activeTab,
    setActiveTab,
    setSteps,
    updateFunnelLocal,
    saveFunnel,
    deleteFunnel,
    refetch: fetchFunnelData,
  }
}
